import { FC, JSX, useEffect, useState } from "react";
import "./ChatHighlightsList.scss";
import MasterPage from "../../MasterPage/MasterPage";
import PageBase from "../../../components/PageBase/PageBase";
import SkeletonTable from "../../../components/SkeletonTable/SkeletonTable";
import { toast } from "react-toastify";
import DropdownMenuTable from "../../../components/DropdownMenuTable/DropdownMenuTable";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faToggleOff,
  faToggleOn,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { useChatHighlights } from "../../../providers/ChatHighlights/ChatHighlightsProvider";
import moment from "moment";
import ChatHighlightForm from "../ChatHighlightForm/ChatHighlightForm";

interface ChatHighlightsListProps {}

const ChatHighlightsList: FC<ChatHighlightsListProps> = () => {
  const [showModalForm, setShowModalForm] = useState<boolean>(false);
  const [selectedHighlight, setSelectedHighlight] = useState<any | null>(null);

  const {
    highlights,
    isLoading,
    error,
    handleList,
    handleActivate,
    handleDeactivate,
    handleDelete,
  } = useChatHighlights();

  const handleEdit = (item: any) => {
    setSelectedHighlight(item);
    setShowModalForm(true);
  };

  const handleInsert = () => {
    setSelectedHighlight(null);
    setShowModalForm(true);
  };

  const handleToggleActive = async (item: any) => {
    const isActive = !!item.is_active;
    const action = isActive ? handleDeactivate : handleActivate;
    const label = isActive ? "desativar" : "ativar";

    const result = await Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja ${label} o destaque <b>${item.title}</b>?</span>`,
      icon: "question",
      confirmButtonText: `Sim, ${label}!`,
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    });

    if (result.isConfirmed) {
      const ret = await action(item.id);
      if (ret) {
        toast.success(`Destaque ${isActive ? "desativado" : "ativado"} com sucesso!`);
        handleList();
      }
    }
  };

  const handleDeleteItem = (item: any) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja excluir o destaque <b>${item.title}</b>? Esta ação não pode ser desfeita.</span>`,
      icon: "warning",
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id);
        if (ret) {
          toast.success("Destaque deletado com sucesso!");
          handleList();
        } else {
          await Swal.fire(
            "Ops!",
            `Não foi possível deletar o destaque.<br>${error}`,
            "error"
          );
        }
      }
    });
  };

  const renderMediaType = (mediaType: string): JSX.Element => {
    if (mediaType === "loom") {
      return <span className="badge bg-info">Loom</span>;
    }
    if (mediaType === "youtube") {
      return <span className="badge bg-danger">YouTube</span>;
    }
    if (mediaType === "video") {
      return <span className="badge bg-warning">Upload</span>;
    }
    if (mediaType === "image") {
      return <span className="badge bg-warning">Imagem</span>;
    }
    return <span className="badge bg-secondary">-</span>;
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {highlights?.length > 0 ? (
          <>
            {highlights.map((x: any, key: number) => (
              <tr key={x.id} className="text-sm">
                <td className="text-center">{x.id}</td>
                <td>{x.title}</td>
                <td>{renderMediaType(x.media_type)}</td>
                <td className="text-center">
                  {x.is_active ? (
                    <span className="badge bg-success">Ativo</span>
                  ) : (
                    <span className="badge bg-secondary">Inativo</span>
                  )}
                </td>
                <td className="text-center">
                  {moment(x.created_at).format("DD/MM/YYYY HH:mm")}
                </td>
                <td className="text-center">
                  <DropdownMenuTable key={key}>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleEdit(x)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Editar
                    </Dropdown.Item>

                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleToggleActive(x)}
                    >
                      <FontAwesomeIcon
                        icon={x.is_active ? faToggleOff : faToggleOn}
                        className="me-2"
                      />
                      {x.is_active ? "Desativar" : "Ativar"}
                    </Dropdown.Item>

                    <Dropdown.Item
                      className="border-radius-md py-2 text-danger"
                      onClick={() => handleDeleteItem(x)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Deletar
                    </Dropdown.Item>
                  </DropdownMenuTable>
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={6} className="text-center text-sm">
              Nenhum destaque cadastrado
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div className="table-responsive">
            <table className="table table-flush m-0 w-100">
              <thead className="thead-light">
                <tr>
                  <th className="text-uppercase opacity-7 text-center" scope="col">
                    #
                  </th>
                  <th className="text-uppercase opacity-7" scope="col">
                    Título
                  </th>
                  <th className="text-uppercase opacity-7" scope="col">
                    Tipo de Mídia
                  </th>
                  <th className="text-uppercase opacity-7 text-center" scope="col">
                    Status
                  </th>
                  <th className="text-uppercase opacity-7 text-center" scope="col">
                    Data de Cadastro
                  </th>
                  <th className="text-uppercase opacity-7 text-center" scope="col">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>{renderList()}</tbody>
            </table>
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  useEffect(() => {
    handleList();
  }, []);

  return (
    <MasterPage title="Destaques do Chat" icon="fas fa-star" footerFixed>
      <div className="ChatHighlightsList" data-testid="ChatHighlightsList">
        <PageBase
          title="Destaques do Chat"
          subtitle="Gerencie os destaques exibidos para os usuários ao entrar no chat."
          handleInsert={handleInsert}
          error={error}
          children={renderContent()}
        />

        <ChatHighlightForm
          show={showModalForm}
          highlight={selectedHighlight}
          onClose={() => {
            setShowModalForm(false);
            setSelectedHighlight(null);
          }}
        />
      </div>
    </MasterPage>
  );
};

export default ChatHighlightsList;
